import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import RBVesselBranch from "../components/RBVesselBranch";
import RoleSwitcher from "../components/RoleSwitcher";
import TerminalReport from "../components/TerminalReport";
import FELLiveMap from "../components/FELLiveMap";
import FleetDashboard from "../components/FleetDashboard";
import ConveyorSystems from "../components/ConveyorSystems";
import TronoxSiloDashboard from "../components/TronoxSiloDashboard";
import CoalTipplerDashboard from "../components/CoalTipplerDashboard";
import NavitradeStockpileDashboard from "../components/NavitradeStockpileDashboard";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api } from "../lib/api";
import {
    Boat,
    Wrench,
    Truck,
    Mountains,
    Gauge,
    Warning,
    CheckCircle,
    ArrowRight,
    Clock,
    ChartLineUp,
    MapPin,
    GitBranch,
    Train,
    Stack,
} from "@phosphor-icons/react";

/* ---------------------------------------------------------------------------
 * Grindrod Richards Bay — Management Portal
 * Top-level operation branches (tabs):
 *   - Overview             : all-operations live summary + alarms
 *   - RB Vessel            : existing vessel loading dashboard
 *   - Coal Tippler         : (stub) — to be specified
 *   - Rock Tippler         : (stub) — to be specified
 *   - Tronox In/Outbound   : (stub) — to be specified
 *
 * Alarms: when any operation has an active breakdown the Overview tile and the
 * tab header pulse red; the Overview tile carries a "Go to operation" link that
 * navigates into the operation branch.
 * ------------------------------------------------------------------------ */

const BRANCHES = [
    { key: "coal-tippler",  label: "Coal Tippler Performance",       short: "Coal Tippler",  Icon: Mountains,  status: "live",     color: "#5A3A22" },
    { key: "rock-tippler",  label: "Rock Tippler Performance",       short: "Rock Tippler",  Icon: Wrench,     status: "upcoming", color: "#374151" },
    { key: "tronox",        label: "Tronox Inbound / Outbound",      short: "Tronox I/O",    Icon: Truck,      status: "live",     color: "#0F766E" },
];


export default function Portal() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const tabFromUrl = params.get("branch");
    const validTabs = ["terminal-report", "rb-vessel", "fel-live", "fleet", "conveyor-systems", "navitrade-stockpile", ...BRANCHES.map((b) => b.key)];
    const [tab, setTab] = useState(tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : "overview");
    const navigate = useNavigate();

    const onTabChange = (value) => {
        // "rail-operations" and "rail-consignments" are separate routes —
        // treat them as navigation triggers so operators land on the
        // dedicated pages instead of an empty tab panel.
        if (value === "rail-operations") { navigate("/rail-operations"); return; }
        if (value === "rail-consignments") { navigate("/rail-consignments"); return; }
        setTab(value);
        if (value === "overview") {
            params.delete("branch");
        } else {
            params.set("branch", value);
        }
        setParams(params, { replace: true });
    };

    return (
        <div className="min-h-screen">
            <Header />
            <main className="max-w-[1600px] mx-auto px-5 py-6">
                <PortalHeader />
                <Tabs value={tab} onValueChange={onTabChange} className="w-full" data-testid="portal-tabs">
                    <TabsList
                        className="!h-auto flex flex-nowrap overflow-x-auto sm:grid sm:grid-cols-9 gap-1 mb-5 bg-slate-100 p-1 rounded-sm w-full justify-start sm:justify-stretch"
                        data-testid="portal-tabs-list"
                    >
                        <PortalTab value="overview" label="Overview" Icon={Gauge} />
                        <PortalTab value="terminal-report" label="Terminal Report" Icon={ChartLineUp} />
                        <PortalTab value="rb-vessel" label="RBTG Vessel Activity" Icon={Boat} />
                        <PortalTab value="rail-operations" label="Rail Operations" Icon={Train} />
                        <PortalTab value="rail-consignments" label="Rail Consignments" Icon={Train} />
                        <PortalTab value="fel-live" label="FEL Live Map" Icon={MapPin} />
                        <PortalTab value="fleet" label="Fleet" Icon={Truck} />
                        <PortalTab value="conveyor-systems" label="Equipment Performance" Icon={GitBranch} />
                        <PortalTab value="navitrade-stockpile" label="Navitrade Stockpile" Icon={Stack} />
                        {BRANCHES.map((b) => (
                            <PortalTab key={b.key} value={b.key} label={b.short} Icon={b.Icon} />
                        ))}
                    </TabsList>

                    <TabsContent value="overview" className="mt-0" data-testid="portal-overview">
                        <OverviewBranch onSelectBranch={onTabChange} />
                    </TabsContent>

                    <TabsContent value="terminal-report" className="mt-0" data-testid="branch-terminal-report">
                        <TerminalReport />
                    </TabsContent>

                    <TabsContent value="fel-live" className="mt-0" data-testid="branch-fel-live">
                        <FELLiveMap />
                    </TabsContent>

                    <TabsContent value="fleet" className="mt-0" data-testid="branch-fleet">
                        <FleetDashboard />
                    </TabsContent>

                    <TabsContent value="conveyor-systems" className="mt-0" data-testid="branch-conveyor-systems">
                        <ConveyorSystems user={user} />
                    </TabsContent>

                    <TabsContent value="rb-vessel" className="mt-0" data-testid="branch-rb-vessel">
                        <RBVesselBranch />
                    </TabsContent>

                    <TabsContent value="navitrade-stockpile" className="mt-0" data-testid="branch-navitrade-stockpile">
                        <NavitradeStockpileDashboard />
                    </TabsContent>

                    <TabsContent value="coal-tippler" className="mt-0" data-testid="branch-coal-tippler">
                        <CoalTipplerDashboard user={user} />
                    </TabsContent>

                    <TabsContent value="rock-tippler" className="mt-0" data-testid="branch-rock-tippler">
                        <ComingSoonBranch
                            title="Rock Tippler Performance"
                            Icon={Wrench}
                            accent="#374151"
                            planned={[
                                "Rake / wagon tipple count per shift against target.",
                                "Live tipple rate with min / max / target markers.",
                                "Breakdown chats tied to each pause, for record-keeping.",
                                "Delay logging and severity tagging (minor / standard / major).",
                                "Shift-wise analytics and exportable reports.",
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="tronox" className="mt-0" data-testid="branch-tronox">
                        <TronoxSiloDashboard />
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}


function PortalHeader() {
    return (
        <div className="mb-5">
            <div className="uppercase-label text-[10px] text-[var(--grindrod-red)] font-semibold tracking-[0.14em]">
                Grindrod · Richards Bay Division
            </div>
            <h1 className="font-display font-black text-2xl md:text-3xl text-[var(--grindrod-navy)] mt-1">
                Management Portal
            </h1>
            <p className="text-sm text-slate-500 mt-1">
                Live performance across every Richards Bay operation — vessels, tipplers, inbound / outbound.
            </p>
        </div>
    );
}


function PortalTab({ value, label, Icon }) {
    return (
        <TabsTrigger
            value={value}
            data-testid={`portal-tab-${value}`}
            className="flex-shrink-0 sm:flex-shrink data-[state=active]:bg-[var(--grindrod-navy)] data-[state=active]:text-white text-[11px] sm:text-xs uppercase tracking-wide font-display font-bold py-2 whitespace-nowrap gap-1.5"
        >
            <Icon size={14} weight="fill" />
            {label}
        </TabsTrigger>
    );
}


/* ---------------- Overview branch ---------------- */

function OverviewBranch({ onSelectBranch }) {
    const [alerts, setAlerts] = useState([]);
    const [vesselSummary, setVesselSummary] = useState({ active: 0, paused: 0 });

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const [aRes, vRes] = await Promise.all([
                    api.get("/alerts/active").catch(() => ({ data: { alerts: [] } })),
                    api.get("/vessels", { params: { status: "active" } }).catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                setAlerts(aRes.data.alerts || []);
                const pausedCount = (vRes.data || []).filter((v) => v.pause_state).length;
                setVesselSummary({ active: (vRes.data || []).length, paused: pausedCount });
            } catch (err) {
                if (!cancelled) console.warn("Portal status load failed:", err?.message || err);
            }
        };
        load();
        const t = setInterval(load, 30_000);
        return () => { cancelled = true; clearInterval(t); };
        // Intentional once-on-mount polling — api is a stable module import,
        // cancelled is a closure-local guard, and load is re-created each render
        // but only the latest reference is used inside the interval.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const vesselAlarms = alerts.filter((a) => ["breakdown_30m", "delay_30m"].includes(a.type));

    return (
        <div className="space-y-5">
            {/* Admin-only role preview switcher (hidden for every other role) */}
            <RoleSwitcher />

            {/* Site-wide status bar */}
            <SiteStatusBar alerts={alerts} />

            {/* Operation tiles */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="portal-overview-tiles">
                <OperationTile
                    branchKey="rb-vessel"
                    title="RBTG Vessel Activity"
                    Icon={Boat}
                    accent="var(--grindrod-navy)"
                    status={vesselAlarms.length > 0 ? "alarm" : vesselSummary.active > 0 ? "live" : "idle"}
                    primary={`${vesselSummary.active} active`}
                    secondary={
                        vesselSummary.paused > 0
                            ? `${vesselSummary.paused} paused · ${vesselAlarms.length} alarm${vesselAlarms.length === 1 ? "" : "s"}`
                            : vesselAlarms.length > 0
                            ? `${vesselAlarms.length} active alarm${vesselAlarms.length === 1 ? "" : "s"}`
                            : "All clear"
                    }
                    onOpen={() => onSelectBranch("rb-vessel")}
                />
                <OperationTile
                    branchKey="coal-tippler"
                    title="Coal Tippler"
                    Icon={Mountains}
                    accent="#5A3A22"
                    status="live"
                    primary="Live"
                    secondary="Track delays · Identify loss · Improve availability"
                    onOpen={() => onSelectBranch("coal-tippler")}
                />
                <OperationTile
                    branchKey="rock-tippler"
                    title="Rock Tippler"
                    Icon={Wrench}
                    accent="#374151"
                    status="upcoming"
                    primary="Coming soon"
                    secondary="Operational specs pending"
                    onOpen={() => onSelectBranch("rock-tippler")}
                />
                <OperationTile
                    branchKey="tronox"
                    title="Tronox I/O"
                    Icon={Truck}
                    accent="#0F766E"
                    status="upcoming"
                    primary="Coming soon"
                    secondary="Operational specs pending"
                    onOpen={() => onSelectBranch("tronox")}
                />
                <OperationTile
                    branchKey="rail-operations"
                    title="Rail Operations"
                    Icon={Truck}
                    accent="#2563EB"
                    status="live"
                    primary="Live"
                    secondary="Track consignments · Mine → TFR → Grindrod"
                    onOpen={() => (window.location.href = "/rail-operations")}
                />
            </div>

            {/* Alarm feed */}
            <AlarmFeed alerts={alerts} onJump={onSelectBranch} />
        </div>
    );
}


function SiteStatusBar({ alerts }) {
    const critical = alerts.filter((a) => a.severity === "critical").length;
    const warnings = alerts.filter((a) => a.severity === "warning").length;
    const info = alerts.filter((a) => a.severity === "info").length;
    const anyAlarm = critical + warnings > 0;
    return (
        <div
            className={`g-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                critical > 0
                    ? "!border-[var(--grindrod-red)] !border-l-[6px] bg-[#FDE7E9]"
                    : warnings > 0
                    ? "!border-[#FFB300] !border-l-[6px] bg-[#FFF8E1]"
                    : "!border-[var(--grindrod-green)] !border-l-[6px] bg-[var(--grindrod-green-bg)]"
            }`}
            data-testid="portal-status-bar"
        >
            {anyAlarm ? (
                <Warning size={28} weight="fill" className={critical > 0 ? "text-[var(--grindrod-red)]" : "text-[#FFB300]"} />
            ) : (
                <CheckCircle size={28} weight="fill" className="text-[var(--grindrod-green)]" />
            )}
            <div className="flex-1">
                <div className="font-display font-black text-lg text-[var(--grindrod-navy)]">
                    {critical > 0
                        ? `${critical} critical alarm${critical === 1 ? "" : "s"} active`
                        : warnings > 0
                        ? `${warnings} warning${warnings === 1 ? "" : "s"} active`
                        : "All operations nominal"}
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                    {critical + warnings + info} event{critical + warnings + info === 1 ? "" : "s"} in the last 6 hours across the terminal
                </div>
            </div>
        </div>
    );
}


function OperationTile({ branchKey, title, Icon, accent, status, primary, secondary, onOpen }) {
    const isAlarm = status === "alarm";
    const isLive = status === "live";
    const isUpcoming = status === "upcoming";
    return (
        <button
            onClick={onOpen}
            className={`g-card text-left overflow-hidden relative group transition-all hover:shadow-md ${
                isAlarm ? "!border-[var(--grindrod-red)] !border-2 animate-pulse-slow" : ""
            }`}
            data-testid={`portal-tile-${branchKey}`}
        >
            {isAlarm && (
                <div className="absolute top-0 right-0 bg-[var(--grindrod-red)] text-white text-[10px] uppercase font-bold px-2 py-1 tracking-wide animate-pulse">
                    Alarm
                </div>
            )}
            <div className="p-4 border-b-[3px]" style={{ borderColor: accent }}>
                <div className="flex items-center gap-2 mb-3">
                    <div
                        className="w-9 h-9 rounded-sm flex items-center justify-center text-white"
                        style={{ background: accent }}
                    >
                        <Icon size={18} weight="fill" />
                    </div>
                    <div className="font-display font-bold text-sm text-[var(--grindrod-navy)] uppercase tracking-wide truncate">
                        {title}
                    </div>
                </div>
                <div className="flex items-end justify-between">
                    <div>
                        <div className="text-2xl font-display font-black text-[var(--grindrod-navy)] tabular">
                            {primary}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{secondary}</div>
                    </div>
                    <StatusPill status={status} />
                </div>
            </div>
            <div className="px-4 py-2.5 flex items-center justify-between text-xs text-[var(--grindrod-navy)] bg-slate-50 group-hover:bg-slate-100">
                <span className="uppercase tracking-wide font-semibold">
                    {isUpcoming ? "View plan" : "Open operation"}
                </span>
                <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </div>
        </button>
    );
}


function StatusPill({ status }) {
    if (status === "alarm") {
        return <span className="g-pill g-pill-red" style={{ fontSize: 9, padding: "2px 6px" }}>ALARM</span>;
    }
    if (status === "live") {
        return <span className="g-pill" style={{ fontSize: 9, padding: "2px 6px", background: "var(--grindrod-green-bg)", color: "#00A344", border: "1px solid var(--grindrod-green)" }}>LIVE</span>;
    }
    if (status === "idle") {
        return <span className="g-pill" style={{ fontSize: 9, padding: "2px 6px", background: "#F1F5F9", color: "#1E293B", border: "1px solid #CBD5E1" }}>IDLE</span>;
    }
    return <span className="g-pill" style={{ fontSize: 9, padding: "2px 6px", background: "#F1F5F9", color: "#64748B", border: "1px dashed #94A3B8" }}>PLANNED</span>;
}


function AlarmFeed({ alerts, onJump }) {
    const sorted = useMemo(() => [...alerts].sort((a, b) => (b.fired_at || "").localeCompare(a.fired_at || "")), [alerts]);
    return (
        <div className="g-card shadow-sm" data-testid="portal-alarm-feed">
            <div className="g-card-header-dark">
                <Warning size={18} weight="fill" />
                <div className="font-display font-bold uppercase tracking-wide">Live Alarm Feed</div>
                <span className="ml-auto text-xs font-semibold tabular">{sorted.length} active</span>
            </div>
            {sorted.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400" data-testid="portal-alarm-empty">
                    No active alarms across Richards Bay operations.
                </div>
            ) : (
                <ul className="divide-y divide-slate-100" data-testid="portal-alarm-list">
                    {sorted.map((a) => {
                        // Currently all alerts are vessel-origin; future operations will extend this routing.
                        const goBranch = () => onJump("rb-vessel");
                        const color =
                            a.severity === "critical"
                                ? "var(--grindrod-red)"
                                : a.severity === "warning"
                                ? "#FFB300"
                                : "var(--grindrod-navy)";
                        return (
                            <li key={a.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
                                <Warning size={20} weight="fill" style={{ color }} />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-[var(--grindrod-navy)] truncate">
                                        {a.message}
                                    </div>
                                    <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                                        <span className="uppercase tracking-wide">{a.type.replace(/_/g, " ")}</span>
                                        <span>·</span>
                                        <Clock size={11} />
                                        <span className="tabular">{a.fired_at ? new Date(a.fired_at).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" }) : "—"}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={goBranch}
                                    className="btn-outline !py-1 !px-2 text-[11px] inline-flex items-center gap-1"
                                    data-testid={`alarm-goto-${a.id}`}
                                >
                                    Open <ArrowRight size={11} weight="bold" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}


/* ---------------- Coming-soon branch ---------------- */

function ComingSoonBranch({ title, Icon, accent, planned }) {
    return (
        <div className="g-card overflow-hidden shadow-sm" data-testid="branch-coming-soon">
            <div className="flex items-stretch">
                <div className="w-16 flex items-center justify-center" style={{ background: accent }}>
                    <Icon size={28} weight="fill" className="text-white" />
                </div>
                <div className="flex-1 bg-[var(--grindrod-navy)] text-white px-5 py-4 flex items-center gap-3">
                    <div>
                        <div className="font-display font-black uppercase tracking-wide text-lg">{title}</div>
                        <div className="text-xs text-slate-300 mt-0.5">Coming to the portal — specs being finalised</div>
                    </div>
                </div>
            </div>
            <div className="p-6">
                <div className="uppercase-label text-[var(--grindrod-red)] mb-3">What this branch will deliver</div>
                <ul className="space-y-2.5" data-testid="coming-soon-list">
                    {planned.map((p, i) => (
                        <li key={typeof p === "string" ? `${p.slice(0, 32)}-${i}` : i} className="flex items-start gap-3">
                            <div
                                className="w-5 h-5 rounded-sm flex-shrink-0 mt-0.5 flex items-center justify-center text-white text-[10px] font-bold tabular"
                                style={{ background: accent }}
                            >
                                {i + 1}
                            </div>
                            <span className="text-sm text-slate-700">{p}</span>
                        </li>
                    ))}
                </ul>
                <div className="mt-6 pt-4 border-t border-slate-100 text-xs text-slate-500">
                    Share your exact metric + shift patterns and this branch will be built out with the same rigour as RB Vessel Performance —
                    live status, breakdown chats, delay logging, analytics, and voice-assistant coverage.
                </div>
            </div>
        </div>
    );
}
